import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { functions } from "../../firebase";

type Person = { uid: string; name: string; email: string; groups: string[]; alreadyMember: boolean };

export function ClubMemberPicker({ clubId, onAdded }: { clubId: string; onAdded: () => Promise<void> }) {
  const [people, setPeople] = useState<Person[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  async function refresh() {
    setLoading(true); setError("");
    try {
      const result = await httpsCallable<{ clubId: string }, { candidates: Person[] }>(functions, "listClubMemberCandidates", { timeout: 120000 })({ clubId });
      setPeople(result.data.candidates); setSelected(new Set());
    } catch (e: any) { setError(e?.message || "Unable to load parents and staff."); }
    finally { setLoading(false); }
  }
  useEffect(() => { void refresh(); }, [clubId]);
  const visible = useMemo(() => people.filter(p =>
    (group === "all" || p.groups.includes(group)) && `${p.name} ${p.email}`.toLowerCase().includes(search.trim().toLowerCase())
  ), [people, group, search]);
  function selectGroup(value: string) {
    setSelected(current => new Set([...current, ...people.filter(p => !p.alreadyMember && (value === "all" || p.groups.includes(value))).map(p => p.uid)]));
  }
  async function add() {
    setSaving(true); setError(""); setStatus("");
    const uids = [...selected]; let added = 0; let existing = 0;
    try {
      const fn = httpsCallable<{ clubId: string; uids: string[] }, { added: number; alreadyMembers: number }>(functions, "addClubMembers", { timeout: 120000 });
      for (let offset = 0; offset < uids.length; offset += 100) {
        const chunk = uids.slice(offset, offset + 100);
        const result = await fn({ clubId, uids: chunk });
        added += result.data.added; existing += result.data.alreadyMembers;
        const completed = new Set(chunk);
        setPeople(current => current.map(p => completed.has(p.uid) ? { ...p, alreadyMember: true } : p));
        setSelected(current => new Set([...current].filter(uid => !completed.has(uid))));
        setStatus(`Processed ${Math.min(offset + 100, uids.length)} of ${uids.length} accounts.`);
      }
      setStatus(`Added ${added} members.${existing ? ` ${existing} were already members.` : ""}`);
      await onAdded();
    } catch (e: any) {
      setError(`${added} members added before this error: ${e?.message || "Unable to finish."} Completed additions are saved. Retry to finish the remaining selection.`);
    } finally { setSaving(false); }
  }
  return <section aria-label="Add parents and staff">
    <h3>Add parents and staff</h3>
    <p className="muted">Choose existing active app accounts. They become club members immediately and can access the club conversation, including its history. Parents and staff without an active app account are not listed; use email invitations below for them.</p>
    {error && <div className="error" role="alert">{error}</div>}
    {status && <div className="success" role="status">{status}</div>}
    <fieldset disabled={loading || saving} style={{ border: 0, padding: 0, minWidth: 0 }}>
      <div className="grid2">
        <label className="label">Search by name or email<input className="input" value={search} onChange={e => setSearch(e.target.value)} /></label>
        <label className="label">Show<select className="input" value={group} onChange={e => setGroup(e.target.value)}><option value="all">Parents and staff</option><option value="parents">Parents</option><option value="staff">Staff</option></select></label>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "12px 0" }}>
        <button type="button" className="btn secondary" onClick={() => selectGroup("parents")}>Select all parents</button>
        <button type="button" className="btn secondary" onClick={() => selectGroup("staff")}>Select all staff</button>
        <button type="button" className="btn secondary" onClick={() => selectGroup("all")}>Select everyone</button>
        <button type="button" className="btn secondary" onClick={() => setSelected(new Set())}>Clear selection</button>
        <button type="button" className="btn secondary" onClick={() => void refresh()}>Refresh list</button>
      </div>
      <p className="muted">{selected.size} selected across all filters. Group selection includes everyone in that group, regardless of search.</p>
      <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
        {loading ? <p className="muted" style={{ padding: 12 }}>Loading parents and staff…</p> : visible.length === 0 ? <p className="muted" style={{ padding: 12 }}>No matching accounts.</p> : visible.map(p =>
          <label key={p.uid} style={{ display: "flex", gap: 12, alignItems: "center", padding: 10, borderBottom: "1px solid var(--border)", background: "var(--card)", color: "var(--text)" }}>
            <input type="checkbox" checked={p.alreadyMember || selected.has(p.uid)} disabled={p.alreadyMember} onChange={e => setSelected(current => { const next = new Set(current); if (e.target.checked) next.add(p.uid); else next.delete(p.uid); return next; })} />
            <span><strong>{p.name}</strong><span className="muted" style={{ display: "block", fontSize: 12 }}>{p.email} · {p.groups.map(g => g === "parents" ? "Parent" : "Staff").join(" / ")}{p.alreadyMember ? " · Already a member" : ""}</span></span>
          </label>)}
      </div>
      <button type="button" className="btn" style={{ marginTop: 12 }} disabled={!selected.size} onClick={() => void add()}>{saving ? "Adding members…" : `Add ${selected.size} selected members`}</button>
    </fieldset>
  </section>;
}
